import { BaseElement, hydrateFromHtml, formatMoney } from "//shop.fieldandstream.com/cdn/shop/t/35/assets/theme.mod.js?v=178699278483456239811786128837"

// ---------------------------------------------------------------------------

/*

* VARIANT CHANGE *
- Drive allocation
- Show appropriate groups only
- Make sure appropriate aria-radio is selected (Triggeres `ARIA RADIO CHANGE` flow)
  - if any subscription radio is selected
    -> Make sure it is supported by variant. Otherwise select the first valid group.
  - if plan is required but one_time radio is selected
    -> Select the first valid group

* ARIA RADIO CHANGE *
- drive selected  subscription group id
- Add [name/required] to the current aria-radio form elements (& remove from other groups)
- Trigger `FREQUENCY/PLAN CHANGE` flow

* FREQUENCY/PLAN CHNAGE *
- Drive plan id / plan
- Update url query param
- Update pricing etc.

*/

const url = new URL(window.location.href)
const DEV = url.searchParams.has('_debug_') || url.searchParams.has('_dev_')

function raise(message, meta) {
  const msg = `[Selling Plan Picker] :: ${message}`
  const err = new Error(msg)
  if (meta) console.log(meta)
  throw err
}

function updateQueryParam(paramsObject) {
  // Get current URL
  const url = new URL(window.location.href)

  for (let param in paramsObject) {
    if (Object.prototype.hasOwnProperty.call(paramsObject, param)) {
      const value = paramsObject[param]

      // Update or remove a param
      if (value == null) {
        url.searchParams.delete(param)
      } else {
        url.searchParams.set(param, value)
      }
    }
  }

  // Now update the current URL
  window.history.replaceState(null, '', url)
}

function invoke(fn) {
  return fn()
}

function parseVariantId(x) {
  if (x && typeof x.id === 'number') x = x.id
  let parsed = typeof x === 'string' ? JSON.parse(x) : x
  if (typeof parsed !== 'number') parsed = null
  return parsed
}

function updateAttribute(element, attr, value) {
  if (value == null) {
    element.removeAttribute(attr)
    return
  }
  element.setAttribute(attr, value)
}

function updateAttributes(element, hashMap) {
  for (let prop in hashMap) {
    if (Object.prototype.hasOwnProperty.call(hashMap, prop)) {
      updateAttribute(element, prop, hashMap[prop])
    }
  }
}

window.__DEV__ = DEV
function print(msg, ...meta) {
  if (!window.__DEV__) return
  const css = `background: palevioletred; color: white; padding: 3px 6px; font-family: monospace;`
  if (meta.length === 0 || meta[0] === undefined) {
    console.log('%c' + msg, css)
  } else {
    console.log('%c' + msg, css, meta)
  }
}

function _each(element, index, callback, pred) {
  if (!element) return

  if (typeof pred === 'function') {
    if (pred(element)) {
      callback(element, index)
    }
  } else {
    callback(element, index)
  }
}

function each(items, callback, pred) {
  if (items instanceof HTMLElement) {
    _each(items, 0, callback, pred)
    return
  }

  if (typeof items.forEach === 'function') {
    items.forEach((item, index) => {
      _each(item, index, callback, pred)
    })
    return
  }

  if (items != null && typeof items === 'object') {
    for (let prop in items) {
      if (Object.prototype.hasOwnProperty.call(items, prop)) {
        _each(items[prop], prop, callback, pred)
      }
    }
    return
  }
}

function selecAriaRadio(selectedRadio, allRadios) {
  allRadios.forEach(radio => {
    radio.ariaChecked = radio === selectedRadio
  })
}

function isAria(element, attr, value) {
  const prop = `aria-${attr}`

  // `isAria(element, 'checked')   =>   `isAria(element, 'checked', 'true')`
  if (value === undefined && (attr === 'disabled' || attr === 'checked')) {
    value = 'true'
  }
  return value == null ? !element.hasAttribute(prop) : element.getAttribute(prop) === value
}

function createAriaRadios({ container, listenerConfig, onSelect, initialRun = false }) {
  let ariaRadiosList =
    container instanceof HTMLElement ? Array.from(container.querySelectorAll('[role="radio"]')) : container
  if (ariaRadiosList instanceof NodeList || ariaRadiosList instanceof HTMLCollection)
    ariaRadiosList = Array.from(ariaRadiosList)

  if (!Array.isArray(ariaRadiosList)) {
    raise('`ariaRadiosList` is not an array', container)
  }

  function onChangeHandler(radio) {
    selecAriaRadio(radio, ariaRadiosList)
    if (onSelect) onSelect(radio)
  }

  const events = ['click', 'keydown']
  ariaRadiosList.forEach(ariaRadio => {
    events.forEach(eventName => {
      ariaRadio.addEventListener(
        eventName,
        e => {
          // is user event
          if (e.isTrusted) {
            if (isAria(ariaRadio, 'disabled')) return
            if (isAria(ariaRadio, 'checked')) return
          }

          if (eventName === 'keydown') {
            if (e.key !== 'Enter') {
              return
            }
          }

          onChangeHandler(ariaRadio)
        },
        listenerConfig
      )
    })
  })

  if (initialRun) {
    let selectedRadio = ariaRadiosList.find(isActiveAriaRadio)
    if (selectedRadio) {
      selecAriaRadio(selectedRadio, ariaRadiosList)
      if (onSelect) onSelect(selectedRadio)
    }
  }

  return onChangeHandler
}

function isActiveAriaRadio(radio) {
  return radio.ariaChecked === 'true' || radio.ariaChecked === true
}

// Array form form elements of same [name]
function read_form_element_value(elements) {
  // Select Element or selectedCheckbox
  const formElement = elements.find(x => {
    if (x instanceof HTMLInputElement) {
      return x.checked
    }
    if (x instanceof HTMLSelectElement) {
      return true
    }
    return false
  })

  return formElement?.value || null
}

function _hasSelectOption(select, value) {
  for (let i = 0; i < select.options.length; i++) {
    if (select.options[i].value === value) return true
  }
  return false
}

function _setFormElementValue(x, value) {
  const t = x.type

  // if `select` element.
  // if value exist, select it. Otherise select the first one
  if (x instanceof HTMLSelectElement) {
    const hasOption = _hasSelectOption(x, value)
    const defaultValue = x.options.length > 0 ? x.options[0].value : ''
    x.value = hasOption ? value : defaultValue
    return
  }

  if (x instanceof HTMLInputElement) {
    if (t !== 'radio' && t !== 'checkbox') {
      x.value = value || ''
      return
    }

    if (t === 'radio' || t === 'checkbox') {
      if (x.value === value) {
        // ?? maybe unselect other checkboxes
        x.checked = true
        return
      }
    }
  }
}

function setFormElementValue(elements, value) {
  if (elements && typeof elements.forEach === 'function') {
    elements.forEach(x => _setFormElementValue(x, value))
    return
  }
  _setFormElementValue(elements, value)
}

function createAllocationByIds(sellingPlans) {
  let sellingPlansById = Object.create(null)

  sellingPlans.selling_plans_by_variant.forEach(x => {
    x.selling_plan_allocations.forEach(alloc => {
      const key = `${x.variant_id} / ${alloc.selling_plan.id}`
      sellingPlansById[key] = alloc
    })
  })

  return function getter(variantId, planId) {
    const key = `${variantId} / ${planId}`
    return sellingPlansById[key] || null
  }
}

function get_price_adjustment_text(alloc) {
  if (!alloc) return ''

  let text = ''
  const price_adjustment = alloc.selling_plan.price_adjustments[0]
  if (price_adjustment) {
    if (price_adjustment.value_type === 'percentage') {
      text = `${price_adjustment.value}%`
    } else if (price_adjustment.value_type === 'fixed_amount') {
      text = formatMoney(price_adjustment.value)
    }
  }

  return text
}

class SellingPlanPicker extends BaseElement {
  static get observedAttributes() {
    return ['variant-id']
  }

  // This gets triggered initial before `connectedCallback`
  attributeChangedCallback(prop, prev, latest) {
    if (!this._isConnected) return
    print('"attributeChangedCallback" triggered', { prop })

    if (prop === 'variant-id') {
      this._onVariantChange(latest)
    }
  }

  constructor() {
    super()
    print('"constructor" triggered')

    this._can_update_query_param = this.getAttribute('update-query-param') === 'false' ? false : true
    this.form_id = this.getAttribute('form-id')
    this.json = JSON.parse(this.querySelector('[data-plans-json]').textContent.trim())
    this.variants = this.json.variants
    this.variants_by_id = this.variants.reduce((o, v) => {
      o[v.id] = v
      return o
    }, Object.create(null))

    this._get_allocation_by_ids = createAllocationByIds(this.json)
  }

  mount() {
    // element hidden?
    // if (this.offsetWidth === 0) return false

    // tick. Wait form elements to load
    // await Promise.resolve()

    print('"mounted" triggered')

    // if (!DEV) {
    //   this.querySelectorAll('[name]').forEach(x => x.removeAttribute('name'))
    //   return
    // }
    // if (DEV) this.removeAttribute('hidden')

    /* Container elements */
    const _el_cont_one = this.querySelector('[data-plan-type="one_time"]')
    const _el_cont_subs = this.querySelector('[data-plan-type="subscription"]')

    // Each spg container
    const _el_conts_spg = Array.from(_el_cont_subs.querySelectorAll('[data-spg-container][data-spg-id]'))

    /* aria-radios */
    const _el_aria_all_radios = Array.from(this.querySelectorAll('[role="radio"]'))
    const el_aria_radio_onetime = _el_aria_all_radios.find(x => x.dataset.value === 'one_time')
    const el_aria_radios_subs = _el_aria_all_radios.filter(x => x.dataset.value === 'subscription')

    // this._selected_aria_radio = _el_aria_all_radios.find(isActiveAriaRadio) || null

    this.els = {
      // containers
      container: {
        one: _el_cont_one,
        subscription: _el_cont_subs,
        subscription_by_spg_id: _el_conts_spg.reduce((o, x) => {
          o[x.dataset.spgId] = x
          return o
        }, Object.create(null)),
        isolatedEffects: this.querySelector('[x-isolated-effects]'),
      },

      ariaRadio: {
        one: el_aria_radio_onetime,
        subscriptions: el_aria_radios_subs,
        subscription_by_spg_id: el_aria_radios_subs.reduce((o, x) => {
          o[x.dataset.spgId] = x
          return o
        }, Object.create(null)),
        all: _el_aria_all_radios,
      },

      // actual form elements with [name="selling_plan"]
      form_elements: {
        one: el_aria_radio_onetime.querySelector('input[data-name="selling_plan"]'),
        subscription: Array.from(_el_cont_subs.querySelectorAll('[data-name="selling_plan"]')),
        subscription_by_spg_id: _el_conts_spg.reduce((o, x) => {
          o[x.dataset.spgId] = Array.from(x.querySelectorAll('[data-name="selling_plan"]'))
          return o
        }, Object.create(null)),
      },
    }

    this._plan_id_by_spg_id = _el_conts_spg.reduce((o, x) => {
      const spg_id = x.dataset.spgId
      o[spg_id] = read_form_element_value(this.els.form_elements.subscription_by_spg_id[spg_id])
      return o
    }, Object.create(null))

    // ------------------------------------------------------------------------------

    this._main()
  }

  // Main body
  _main() {
    if (this._isConnected) return

    print('"_main" triggered')

    // Handle aria-radios
    this._on_select_group = createAriaRadios({
      container: this,
      listenerConfig: { signal: this._ac.signal },

      /*
      * ARIA RADIO CHANGE *
      - drive selected  subscription group id
      - Add [name/required] to the current aria-radio form elements (& remove from other groups)
      - Trigger `FREQUENCY/PLAN CHANGE` flow
     */
      onSelect: ariaRadio => {
        print('"createAriaRadios.onSelect" triggered', ariaRadio)

        this._selected_aria_radio = ariaRadio
        this._deriveGroupId()

        // Remove `name` from all subscription groups
        each(this.els.form_elements.subscription, el => {
          el.removeAttribute('name')
          el.removeAttribute('form')
          el.removeAttribute('required')
        })

        const radioType = this._selected_aria_radio.dataset.value

        if (radioType === 'one_time') {
          this.els.form_elements.one.setAttribute('name', 'selling_plan')
          this.els.form_elements.one.checked = true
          if (this.form_id) {
            this.els.form_elements.one.setAttribute('form', this.form_id)
          }
        } else {
          this.els.form_elements.one.removeAttribute('name')
          this.els.form_elements.one.removeAttribute('form')
          this.els.form_elements.one.checked = false

          if (!this._spg_id) {
            raise('Unreachable branch. Expected `_spgId` to be defined.', {
              'this._spg_id': this._spg_id,
              'this._selected_aria_radio': this._selected_aria_radio,
            })
          }

          // Add [name/required] to the selected subscription group only
          each(this.els.form_elements.subscription_by_spg_id[this._spg_id], el => {
            el.setAttribute('name', 'selling_plan')
            el.setAttribute('required', 'true')
            if (this.form_id) {
              el.setAttribute('form', this.form_id)
            }
          })
        }

        // Trigger frequency change
        this._onFrequencyChange(true)
      },
    })

    // Trigger variant change once on load
    this._onVariantChange()

    // Update selling_plan query param when frequency changes
    this.els.container.subscription.addEventListener(
      'change',
      e => {
        const target = e.target
        if (target.name !== 'selling_plan') return

        const value = target.value || null
        this._plan_id = value

        if (this._spg_id) {
          this._plan_id_by_spg_id[this._spg_id] = value
        }

        this._onFrequencyChange()
      },
      { signal: this._ac.signal }
    )

    // sync variant id
    const productForm = document.forms[this.form_id]
    productForm?.addEventListener(
      'variant:change',
      event => {
        this.setAttribute('variant-id', parseVariantId(event.detail.variant?.id))
      },
      { signal: this._ac.signal }
    )
  }

  /*
    VARIANT CHANGE:
    - Redrive allocation
    - Show appropriate spg containers only. Toggle visible if variant supports it or not
    - Make sure appropriate aria-radio is selected
      -- If not appropriate radio is selected - select it (Triggeres `ARIA RADIO CHANGE` flow)
  */
  _onVariantChange() {
    print('"_onVariantChange" triggered')
    this._deriveVariantData()

    this._showValidGroupsOnly()

    // Triggers aria-radio flow
    this._makeSureAriaRadioIsCorrect()
  }

  _deriveVariantData(id = this.getAttribute('variant-id')) {
    this._variant_id = parseVariantId(id)
    this._variant_allocs = this.json?.selling_plans_by_variant.find(x => x.variant_id === this._variant_id) || []

    // These are all the valid selling plan group ids for this variant
    const variant_spg_ids_set = new Set()
    this._variant_allocs?.selling_plan_allocations.forEach(alloc => {
      const id = alloc.selling_plan_group_id
      if (id) variant_spg_ids_set.add(id)
    })
    this._variant_spg_ids = Array.from(variant_spg_ids_set)
  }

  _showValidGroupsOnly() {
    each(this.els.container.subscription_by_spg_id, (x, spgId) => {
      const supported = this._variant_spg_ids.includes(spgId)
      // show container if supported - else hide
      updateAttributes(x, {
        hidden: supported ? null : '',
      })

      const ariaRadioOfSpg = this.els.ariaRadio.subscription_by_spg_id[spgId]
      // enable radio if supported - else disable
      updateAttribute(ariaRadioOfSpg, {
        disabled: supported ? null : '',
        'aria-disabled': supported ? null : 'true',
      })
    })
  }

  // must trigger aria-radio flow by calling `this._on_select_group` or by dispatching `click` event
  _makeSureAriaRadioIsCorrect() {
    this._getSelectedAriaRadio()

    const plan_required = this._variant_allocs?.requires_selling_plan
    const firstValidGroupId = this._ensuredFirstValidGroupId()

    // Update one_time aria radio
    updateAttributes(this.els.ariaRadio.one, {
      disabled: plan_required ? '' : null,
      'aria-disabled': plan_required ? 'true' : null,
    })
    this.classList.toggle('spp--one-time-disabled', plan_required)

    // no selected radio yet
    if (!this._selected_aria_radio) {
      const radio = plan_required
        ? this.els.ariaRadio.subscription_by_spg_id[firstValidGroupId]
        : this.els.ariaRadio.one
      // radio.dispatchEvent(new Event('click'))
      this._on_select_group(radio)
      return
    }

    const radioType = this._selected_aria_radio.dataset.value

    if (radioType === 'one_time') {
      if (!plan_required) {
        // this.els.ariaRadio.one.dispatchEvent(new Event('click'))
        this._on_select_group(this.els.ariaRadio.one)
        return
      }

      // plan is required, but 'one_time' radio is selected
      // select the first valid subscription group and select it
      const subscriptionRadio = this.els.ariaRadio.subscription_by_spg_id[firstValidGroupId]
      // reset value
      const ariaRadioFormEls = this.els.form_elements.subscription_by_spg_id[subscriptionRadio.dataset.spgId]
      setFormElementValue(ariaRadioFormEls, '')

      //subscriptionRadio.dispatchEvent(new Event('click'))
      this._on_select_group(subscriptionRadio)
      return
    }

    if (radioType === 'subscription') {
      // Make sure id is valid
      const selectedSpgId = this._selected_aria_radio.dataset.spgId
      const validId = this._variant_spg_ids.includes(selectedSpgId)

      if (validId) {
        // this._selected_aria_radio.dispatchEvent(new Event('click'))
        this._on_select_group(this._selected_aria_radio)
        return
      }

      // Invalid id - select the first valid group
      const subscriptionRadio = this.els.ariaRadio.subscription_by_spg_id[firstValidGroupId]

      // reset value
      const ariaRadioFormEls = this.els.form_elements.subscription_by_spg_id[subscriptionRadio.dataset.spgId]
      setFormElementValue(ariaRadioFormEls, '')

      // subscriptionRadio.dispatchEvent(new Event('click'))
      this._on_select_group(subscriptionRadio)
      return
    }

    raise('Unreachable branch. Selected aria-radio value is not correct. Must be one of "one_time" | "subscription"')
  }

  _deriveGroupId() {
    const plan_required = this._variant_allocs?.requires_selling_plan
    const selectedAriaRadio = this._getSelectedAriaRadio()

    const firstValidGroupId = this._ensuredFirstValidGroupId()

    // if subscription radio is selected
    const radioType = selectedAriaRadio.dataset.value
    if (radioType === 'subscription') {
      // but currently selected group is invalid or empty
      // then re-select it
      if (this._spg_id == null || !this._variant_spg_ids.includes(this._spg_id)) {
        this._spg_id = firstValidGroupId
      }
    } else if (radioType === 'one_time') {
      // if one-time radio is selected but selling plan is required
      if (plan_required) {
        // select the first valid group id
        this._spg_id = firstValidGroupId
      } else {
        this._spg_id = null
      }
    }
  }

  _derivePlanData(read_from_dom = false) {
    if (this._spg_id) {
      if (read_from_dom) {
        const group_form_els = this.els.form_elements.subscription_by_spg_id[this._spg_id]
        this._plan_id = read_form_element_value(group_form_els)
      }

      this._selected_alloc = this._get_allocation_by_ids(this._variant_id, this._plan_id)
      this._plan_id_by_spg_id[this._plan_id]
    } else {
      this._plan_id = null
      this._selected_alloc = null
    }
  }

  /*
  *  FREQUENCY/PLAN CHNAGE *
    - Drive plan id / plan
    - Update url query param
    - Update pricing etc.
  */
  _onFrequencyChange(read_from_dom = false) {
    this._derivePlanData(read_from_dom)

    this._updateQueryParam()

    print('Plan changed .. Update pricing', { id: this._plan_id, plan: this._selected_alloc })

    const selected_alloc_price_adjustment_text = get_price_adjustment_text(this._selected_alloc)
    const baseContext = [
      ['formatMoney', formatMoney],
      ['variant', this._variant_id ? this.variants_by_id[this._variant_id] : null],

      ['selected_alloc', this._selected_alloc],
      ['selected_alloc_price_text', selected_alloc_price_adjustment_text],
    ]

    hydrateFromHtml({
      container: this.els.container.one,
      preferCachedChildren: true,
      context: baseContext,
    })

    each(this.els.container.subscription_by_spg_id, (el, spg_id) => {
      const current_spg = this._spg_id === spg_id
      const current_spg_plan_id = this._plan_id_by_spg_id[spg_id]
      const current_alloc =
        this._get_allocation_by_ids(this._variant_id, current_spg_plan_id) || this._variant_allocs[0]
      const current_alloc_price_adjustment_text = get_price_adjustment_text(current_alloc)

      hydrateFromHtml({
        container: el,
        preferCachedChildren: true,
        context: [
          ...baseContext,
          ['current_alloc', current_alloc],
          ['current_alloc_price_adjustment_text', current_alloc_price_adjustment_text],
        ],
      })
    })

    if (this.els.container.isolatedEffects) {
      hydrateFromHtml({
        container: this.els.container.isolatedEffects,
        preferCachedChildren: true,
        context: baseContext,
      })
    }
  }

  _getSelectedAriaRadio() {
    if (this._selected_aria_radio) return this._selected_aria_radio
    this._selected_aria_radio = this.querySelector('[role="radio"][aria-checked="true"]')
    return this._selected_aria_radio
  }

  _updateQueryParam() {
    if (this._can_update_query_param) {
      updateQueryParam({
        variant: this._variant_id,
        selling_plan: this._plan_id,
      })
    }
  }

  _ensuredFirstValidGroupId() {
    const plan_required = this._variant_allocs?.requires_selling_plan
    const firstValidGroupId = this._variant_spg_ids[0]
    if (plan_required && !firstValidGroupId) {
      raise('Unreachable branch. Plan required but no valid group id found', {
        'this._variant_allocs': this._variant_allocs,
        'this._variant_spg_ids': this._variant_spg_ids,
      })
    }
    return firstValidGroupId
  }
}

if (!window.customElements.get('selling-plan-picker')) {
  window.customElements.define('selling-plan-picker', SellingPlanPicker)
}
